import events from "_src/engine/events";
import output from "_src/engine/output";
import { cullingFrontFace } from "_src/engine/constants";
import { createMirrorMatrix } from "_src/engine/math"
import minmaxUtils from "_src/engine/utility/minmax";

import { mat4, vec3 } from "_src/libs/gl-matrix";
import { basePlane, stepType } from "_src/constants";
import windowManager from "_src/client/window_manager";
import renderGraph from "_src/render_graph/render_graph";
import history from "_src/history/manager";
import preview from "_src/client/preview";

const mirrors = [];

const mirrorPlaneEnabled = false;

export default {

  preview: (function () {
    const mat4Tmp = mat4.create();

    return function (objects, point, plane, scene, visibility) {

      const mainWindow = windowManager.getWindow(windowManager.MAIN);

      mirrors.forEach((object) => {
        mainWindow.removeObject(object);
        scene.remove(object.model);
      }, scene);
      mirrors.length = 0;

      const mainRenderGraph = mainWindow.renderGraph;
      mainRenderGraph.setEnabled(renderGraph.MIRROR, mirrorPlaneEnabled && visibility);

      if (!visibility) {
        return;
      }

      let minmaxWorld = [Infinity, Infinity, Infinity, -Infinity, -Infinity, -Infinity];
      for (let object of objects) {

        let mirrorMatrix = createMirrorMatrixProxy(mat4Tmp, point, plane);
        if (!mirrorMatrix) {
          return;
        }

        const mirroredObject = preview.createPreview(object, mirrorMatrix);
        const oldSign = object.model.scale.reduce((accumulator, currentValue) => accumulator * currentValue);
        mirroredObject.model.shader.setCullingFrontFace(oldSign > 0 ? cullingFrontFace.COUNTER_CLOCK_WISE : cullingFrontFace.CLOCK_WISE);

        //order does matter. First add to scene, then - to window
        scene.add(mirroredObject.model);
        mirrors.push(mirroredObject);
        mainWindow.addObject(mirroredObject);

        minmaxUtils.expandMinmax(minmaxWorld, object.getWorldMinmax());
      }

      events.emit("object_bboxes_updated", mirrors);

      if (!mirrorPlaneEnabled) {
        return;
      }

      let mirrorPlaneMatrix = createMirrorPlaneMatrix(mat4Tmp, point, plane, minmaxWorld);
      if (!mirrorPlaneMatrix) {
        return;
      }
      let mirrorPlaneModel = mainRenderGraph.getPass(renderGraph.MIRROR).getLastScene().getChildren()[0];
      mirrorPlaneModel.setMatrix(mirrorPlaneMatrix);
    }
  })(),

  apply: (function () {
    const mat4Tmp = mat4.create();

    return function (objects, point, plane) {
      let mirrorMatrix = createMirrorMatrixProxy(mat4Tmp, point, plane);
      if (!mirrorMatrix) return false;

      for (const object of objects) {
        history.writeObjectToCurrentStep(object);
        object.model.modelMatrix = mat4.multiply(object.model.modelMatrix, mirrorMatrix, object.model.modelMatrix);
        object.move(object.model.modelMatrix, true);
        events.emit("object_moved", object);
      }
      history.addStep(stepType.TRANSFORM);
      events.emit("objects_updated", objects);
      return true;
    }
  })(),

  cleanup() {
    mirrors.length = 0;
  },
}

const createMirrorPlaneMatrix = (function() {
  const vec3Tmp = vec3.create();
  return function (mirrorPlaneMatrix, point, planeEnum, minmaxWorld) {
    let bboxSize = [];
    let center = [];
    for (let i=0;i<3;i++){
      bboxSize.push(minmaxWorld[i+3]-minmaxWorld[i]);
      center.push((minmaxWorld[i+3]+minmaxWorld[i])/2);
    }

    switch (planeEnum) {
      case basePlane.XOY: {
        mat4.fromTranslation(mirrorPlaneMatrix, vec3.set(vec3Tmp, center[0], center[1], point[2]));
        mat4.scale(mirrorPlaneMatrix, mirrorPlaneMatrix, vec3.set(vec3Tmp, bboxSize[0], bboxSize[1], 1));
        break;
      }
      case basePlane.YOZ: {
        mat4.fromTranslation(mirrorPlaneMatrix, vec3.set(vec3Tmp, point[0], center[1], center[2]));
        mat4.scale(mirrorPlaneMatrix, mirrorPlaneMatrix, vec3.set(vec3Tmp, 1, bboxSize[1], bboxSize[2]));
        mat4.rotateY(mirrorPlaneMatrix, mirrorPlaneMatrix, Math.PI/2);
        break;
      }
      case basePlane.ZOX: {
        mat4.fromTranslation(mirrorPlaneMatrix, vec3.set(vec3Tmp, center[0], point[1], center[2]));
        mat4.scale(mirrorPlaneMatrix, mirrorPlaneMatrix, vec3.set(vec3Tmp, bboxSize[0], 1, bboxSize[2]));
        mat4.rotateX(mirrorPlaneMatrix, mirrorPlaneMatrix, Math.PI/2);
        break;
      }
      default :
        output.error(output.API_ERROR, "Can't apply/preview mirroring. Wrong mirroring plane: \"" + planeEnum + "\"");
        return null;
    }

    return mirrorPlaneMatrix;
  }
})();

function createMirrorMatrixProxy(mirrorMatrix, point, planeEnum) {
  switch (planeEnum) {
    case basePlane.XOY :
      createMirrorMatrix(mirrorMatrix, [0,0,-1,point[2]]);
      break;
    case basePlane.YOZ :
      createMirrorMatrix(mirrorMatrix, [-1,0,0,point[0]]);
      break;
    case basePlane.ZOX :
      createMirrorMatrix(mirrorMatrix, [0,-1,0,point[1]]);
      break;
    default :
      output.error(output.API_ERROR, "Can't apply/preview mirroring. Wrong mirroring plane: \"" + planeEnum + "\"");
      return null;
  }
  return mirrorMatrix;
}
